import { useCallback, useMemo } from 'react'

import { MessageSequenceStatus } from '@api'

import { useToast } from '@/components/ui/toast.tsx'
import {
  useCreateExpiringFansMessageMutation,
  useDeleteExpiringFansMessageMutation,
  useExpiringFansMessages,
  useExpiringFansSettings,
  useMarkAsMessaged,
  useToggleExpiringFansMessageByMessagId,
  useUpdateExpiringFanSettings,
  useUpdateExpiringFansMessageMutation,
} from '@/modules/expiring-fans/query/expiring-fans-query'
import type { ExpiringFanSettingsForm } from '@/modules/expiring-fans/service/expiring-fans-service.ts'
import { handleError } from '@/utils/error'
import { mutate } from '@/utils/mutate.ts'

export function useExpiringFanSettings() {
  const markAsMessaged = useMarkAsMessaged()
  const toast = useToast()

  const { data: autoMessageExpiringFans } = useExpiringFansSettings()
  const updateSettings = useUpdateExpiringFanSettings()

  const { data: messages } = useExpiringFansMessages()
  const createExpiringFansMessageMutation =
    useCreateExpiringFansMessageMutation()
  const updateExpiringFansMessageMutation =
    useUpdateExpiringFansMessageMutation()
  const deleteExpiringFansMessageMutation =
    useDeleteExpiringFansMessageMutation()

  const toggleWelcomeMessageByMessagId =
    useToggleExpiringFansMessageByMessagId()

  const handleToggleChange = async (id: number, value: boolean) => {
    return toggleWelcomeMessageByMessagId.mutateAsync({
      messageId: String(id),
      value: value
        ? MessageSequenceStatus.ACTIVE
        : MessageSequenceStatus.INACTIVE,
    })
  }

  const update = useCallback(
    (field: Partial<ExpiringFanSettingsForm>) => {
      if (!autoMessageExpiringFans) {
        return
      }
      const newSetting: ExpiringFanSettingsForm = {
        ...autoMessageExpiringFans,
        ...field,
      }

      return updateSettings
        .mutateAsync(newSetting)
        .then((response) => {
          toast.success('Settings updated successfully')

          return response
        })
        .catch((error) => {
          handleError(error, 'Settings update failed')
          console.warn('Settings update failed', error)
          throw error
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toast, updateSettings],
  )

  return useMemo(
    () => ({
      messages: messages || [],
      enabled: autoMessageExpiringFans?.enabled || false,
      hoursBeforeExpiry: autoMessageExpiringFans?.hoursBeforeExpiry || 0,
      maxSpend: autoMessageExpiringFans?.maxSpend || 0,
      minHoursSinceLastMessage:
        autoMessageExpiringFans?.minHoursSinceLastMessage || 0,
      markAsMessaged: (fanId: number) =>
        markAsMessaged.mutateAsync({ fanId, messaged: true }),
      markAsNotMessaged: (fanId: number) =>
        markAsMessaged.mutateAsync({ fanId, messaged: false }),
      isMarkingAsMessaged: markAsMessaged.isLoading,
      isUpdating: updateSettings.isLoading,
      deleteMessage: mutate(deleteExpiringFansMessageMutation),
      changeEnabled: (enabled: boolean) => update({ enabled }),
      changeMaxSpend: (maxSpend: number | null) => update({ maxSpend }),
      changeHoursBeforeExpiry: (hoursBeforeExpiry: number) =>
        update({ hoursBeforeExpiry }),
      changeMinHoursSinceLastMessage: (minHoursSinceLastMessage: number) =>
        update({ minHoursSinceLastMessage }),
      handleToggleChange,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      autoMessageExpiringFans,
      createExpiringFansMessageMutation,
      deleteExpiringFansMessageMutation,
      markAsMessaged,
      messages,
      update,
      updateExpiringFansMessageMutation,
      updateSettings.isLoading,
    ],
  )
}
